/**
 * useAuthentication Hook
 *
 * React hook for authentication state management.
 * Accepts an `initialSession` (pre-validated by readPaymentSession in main.tsx)
 * and stores credentials in AuthenticationService memory.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiClient } from '../api/client';
import { AuthenticationService } from '../services/auth/AuthenticationService';
import type { AuthState } from '../types/auth.types';
import { logger } from '../utils/logger';
import { maskCredential } from '../utils/maskCredential';
import { SESSION_CONFIG } from '../utils/constants';

/**
 * useAuthentication Options
 */
export interface UseAuthenticationOptions {
  skip?: boolean;
}

/**
 * useAuthentication Hook
 *
 * Manages authentication lifecycle:
 * - Accepts pre-validated credentials via initialSession prop
 * - Stores credentials in AuthenticationService
 * - Tracks session expiry
 * - Provides retry mechanism
 *
 * @param baseUrl - API base URL
 * @param options - Options (skip for mock mode)
 * @param initialSession - Pre-validated session from window.__PAYMENT_SESSION__ (read in main.tsx)
 * @returns Auth state and service instance
 */
export function useAuthentication(
  baseUrl: string,
  options: UseAuthenticationOptions = {},
  initialSession?: PaymentSession
) {
  const { skip = false } = options;

  // Stable ref so authenticate() always sees the latest initialSession without
  // being recreated (and re-triggering useEffect) on every new object reference.
  const initialSessionRef = useRef(initialSession);
  initialSessionRef.current = initialSession;

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: skip,
    isLoading: !skip,
    error: null,
  });

  const [{ authService, apiClient }] = useState(() => {
    const inst = new ApiClient(baseUrl);
    return { authService: new AuthenticationService(inst), apiClient: inst };
  });

  /**
   * Load credentials from initialSession
   */
  const authenticate = useCallback(async () => {
    if (skip) {
      return;
    }

    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    const session = initialSessionRef.current;

    if (!session) {
      logger.error(
        'initialSession is not set — BFF must embed credentials in HTML'
      );
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: new Error(
          'Payment session credentials not found. Please refresh the page.'
        ),
      });
      return;
    }

    const { sessionId, apiKey, paymentId, agentSessionId, expiresAt } = session;

    // Guard: ensure required auth credential fields are non-empty. readPaymentSession()
    // validates these when credentials come from main.tsx, but useAuthentication is also
    // called directly in tests and integration harnesses that bypass readPaymentSession.
    // Note: amount/currency are session display fields, not auth credentials — they are
    // not validated here and not stored in AuthenticationService.
    const requiredFields = {
      sessionId,
      apiKey,
      paymentId,
      agentSessionId,
      expiresAt,
    };
    const emptyField = (
      Object.keys(requiredFields) as Array<keyof typeof requiredFields>
    ).find((k) => !requiredFields[k]?.trim());
    if (emptyField) {
      logger.error(`useAuthentication: session field '${emptyField}' is empty`);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: new Error(
          'Incomplete payment session credentials. Please refresh the page.'
        ),
      });
      return;
    }

    // Guard against retrying with an already-expired session.
    // NaN <= Date.now() is false, so check isNaN first to catch invalid date strings.
    const expiryTime = new Date(expiresAt).getTime();
    if (Number.isNaN(expiryTime) || expiryTime <= Date.now()) {
      logger.error('Cannot authenticate: session has already expired', {
        expiresAt,
      });
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: new Error('Session expired. Please refresh the page.'),
      });
      return;
    }

    try {
      authService.setCredentials({
        sessionId,
        apiKey,
        paymentId,
        agentSessionId,
        expiresAt,
      });

      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        error: null,
        expiresAt,
      });

      logger.info('Authentication successful via initialSession', {
        sessionId: maskCredential(sessionId),
      });
    } catch (error) {
      logger.error('Failed to set credentials', error);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error:
          error instanceof Error ? error : new Error('Authentication failed'),
      });
    }
    // initialSession is accessed via ref to avoid re-creating this callback (and re-running
    // the mount effect) on every parent render that produces a new object reference.
    // Use refresh(newSession) to re-authenticate with a rotated token — prop changes alone
    // will NOT re-trigger authentication with this ref pattern.
  }, [authService, skip]);

  /**
   * Monitor session expiry
   */
  useEffect(() => {
    if (skip || !authState.isAuthenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      if (authService.isExpired() || !authService.isAuthenticated()) {
        logger.warn('Session expired');

        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: false,
          error: new Error('Session expired. Please refresh the page.'),
        }));

        authService.clearCredentials();
        return;
      }

      // Single getAuthState() call — avoids redundant date parses per tick.
      const { timeRemaining } = authService.getAuthState();

      setAuthState((prev) => ({ ...prev, timeRemaining }));

      if (timeRemaining !== null && timeRemaining !== undefined) {
        const warningMs = SESSION_CONFIG.EXPIRY_WARNING_MINUTES * 60 * 1000;
        if (timeRemaining <= warningMs) {
          const minutesRemaining = Math.ceil(timeRemaining / 60000);
          logger.warn(`Session expires in ${minutesRemaining} minute(s)`);
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [skip, authState.isAuthenticated, authService]);

  /**
   * Load credentials on mount
   */
  useEffect(() => {
    authenticate();
  }, [authenticate]);

  /**
   * Retry loading credentials (re-reads current initialSessionRef).
   */
  const retry = useCallback(() => {
    authenticate();
  }, [authenticate]);

  /**
   * Re-authenticate with a new (rotated) session.
   * Replaces the ref immediately so authenticate() picks up the new token.
   */
  const refresh = useCallback(
    (newSession: PaymentSession) => {
      initialSessionRef.current = newSession;
      authenticate();
    },
    [authenticate]
  );

  return {
    authState,
    authService,
    apiClient,
    retry,
    refresh,
  };
}
